import React from "react";
import {
  Box,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { IconButton } from "../../components/ui/Button";
import { useDispatch,useSelector, useStore } from "react-redux";
import { AppDispatch,RootState } from "../../services/redux/store";
import { nextStep } from "../../services/redux/slices/stepperSlice";
import { setActiveHorizontalIndex } from "../../services/redux/slices/stepperDynamicFormSlice";
import { setStatusPopup } from "../../services/redux/slices/statusPopupSlice";
import { useNavigate, useLocation } from "react-router-dom";
import {
  getCombinedApplicationSteps,
} from "../../components/ui/StepperDyanmicForm/helper";
import {
  setDisplayHorizontalSteps,
} from "../../services/redux/slices/stepperDynamicFormSlice";
import { fetchUserApplicationsFormData } from "../../services/api/service/userApplicationService";
const CompletionStatus: React.FC<{ isContinueButtonDisabled: boolean }> = ({
  isContinueButtonDisabled,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const store = useStore<RootState>();
  const {
      trackingId,
    } = useSelector((state: RootState) => state.stepper);
  const handleContinue = async() => {
    localStorage.setItem("coAppId", "0");
    const token: any = process.env.REACT_APP_FETCH_TOKEN;
    const res: any = await fetchUserApplicationsFormData(
      token,
      trackingId
    );
    const apps = res.data?.applications || [];
    let finalSteps = getCombinedApplicationSteps(apps[0]);
    dispatch(setDisplayHorizontalSteps(finalSteps));
    if (!isContinueButtonDisabled) {
      // Clean up document_verified and consent parameters from URL
      const currentSearch = location?.search || "";
      let cleanSearch = currentSearch
        .replace(/[?&]document_verified=true/g, "")
        .replace(/[?&]consent=true/g, "");
      
      // Clean up any double ampersands or leading ampersand
      cleanSearch = cleanSearch.replace(/^\?&/, "?").replace(/^&/, "?").replace(/&&/g, "&");
      
      // If cleanSearch is just "?", remove it
      if (cleanSearch === "?") {
        cleanSearch = "";
      }
      
      const newUrl = `${window.location.pathname}${cleanSearch}`;
      navigate(newUrl, { replace: true });
      
      // Then dispatch to change the active step
      dispatch(setActiveHorizontalIndex(3));
    }
  };

  // Mobile view that matches the image exactly
  if (isMobile) {
    return (
      <Box
        sx={{
          mb: 3,
          padding: "12px",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#ccf0f0",
          borderRadius: "8px",
          position: "relative",
        }}
        data-testid="completion-status-container"
      >
        {/* Progress Message */}
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 600,
            color: "rgba(31, 34, 51, 1)", // Dark teal color
            fontFamily: "Jost, sans-serif",
            lineHeight: "1.4",
            mb: 2,
            maxWidth: "100%",
          }}
        >
          You are super close to making your academic dream a reality. The
          remaining steps will take
        </Typography>

        {/* Highlighted Time */}
        <Typography
          sx={{
            fontSize: "18px",
            fontWeight: 800,
            color: "rgba(254, 193, 13, 1)", // Bright orange color
            fontFamily: "Jost, sans-serif",
            mb: 2,
            lineHeight: "1.2",
          }}
        >
          just another 10 minutes.
        </Typography>

        {/* Continue Button */}
        <Button
          variant="contained"
          onClick={handleContinue}
          sx={{
            backgroundColor: "#ffd700", // Bright yellow
            color: "#000000", // Black text
            fontSize: "16px",
            fontWeight: 700,
            fontFamily: "Jost, sans-serif",
            borderRadius: "8px",
            padding: "12px 32px",
            textTransform: "none",
            boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
            "&:hover": {
              backgroundColor: "#ffed4e",
              boxShadow: "0px 6px 12px rgba(0, 0, 0, 0.15)",
            },
          }}
        >
          Continue
        </Button>
      </Box>
    );
  }

  // Desktop view - keep existing unchanged
  return (
    <Box
      sx={{
        padding: "24px",
        mb: 3,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        backgroundColor: "#ccf0f0", // Light blue background
      }}
      data-testid="completion-status-container"
    >
      <Typography
        className="text1"
        sx={{
          maxWidth: { xs: "95%", sm: "80%" }, // Narrower text on small screens
          lineHeight: { xs: 1.4, sm: 1.5 },
          fontSize: { xs: "11", sm: "13px" }, // Adjust body2 size for mobile (defaults to 0.875rem)
        }}
      >
        You are super close to making your academic dream a reality. The
        remaining steps will take{" "}
        <span className="text4Orange"> just another 10 minutes.</span>
      </Typography>
      <IconButton>
        <span
          onClick={handleContinue}
        >
          Continue Application
        </span>
      </IconButton>
    </Box>
  );
};

export default CompletionStatus;
